"use client";

import { Suspense, useEffect, useRef } from "react";
import { lazyWithReload } from "@/lib/utils/lazy-with-reload";
import { Camera, ShieldAlert, MicOff, Monitor } from "lucide-react";
import { Button } from "@speakai/ui/button";
import { cn } from "@/lib/utils/cn";
import { formatClock } from "@speakai/common/src/utils/clock";
import { useTranslation } from "react-i18next";
import type { WavesurferMicPlugin } from "@/features/recorder/hooks/use-recording-engine";
import { PREVIEW_FRAME_CLASS, VIDEO_STYLE } from "./video-style";

// WaveSurfer requires the Web Audio API — loaded lazily (browser-only).
const AudioWaveformRecording = lazyWithReload(() => import("./audio-waveform-recording"), "AudioWaveformRecording");

// Canvas + RAF compositor is browser-only.
const ScreenShareComposer = lazyWithReload(() => import("./screen-share-composer"), "ScreenShareComposer");

type MicBannerVariant = "hardware" | "mute";

interface MicStatusBannerProps {
  variant: MicBannerVariant;
  message: string;
  onResume?: () => void;
  resumeLabel: string;
}

// Mute variant uses text-warning (not text-warning-foreground) — text-warning-foreground
// fails WCAG AA on bg-warning/10 in dark mode.
function MicStatusBanner({
  variant,
  message,
  onResume,
  resumeLabel,
}: MicStatusBannerProps) {
  const isHardware = variant === "hardware";
  const Icon = isHardware ? ShieldAlert : MicOff;
  return (
    <div
      role={isHardware ? "alert" : "status"}
      className={cn(
        "rounded-lg p-4 flex gap-3 items-start border",
        isHardware
          ? "border-destructive/30 bg-destructive/10"
          : "border-warning/30 bg-warning/10",
      )}
    >
      <Icon
        className={cn(
          "h-5 w-5 flex-shrink-0 mt-0.5",
          isHardware ? "text-destructive" : "text-warning",
        )}
        aria-hidden="true"
      />
      <div className="flex-1">
        <p
          className={cn(
            "font-semibold",
            isHardware ? "text-destructive" : "text-warning",
          )}
        >
          {message}
        </p>
      </div>
      {onResume && (
        <Button
          type="button"
          size="sm"
          variant={isHardware ? "danger" : "outline"}
          onClick={onResume}
          aria-label={resumeLabel}
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {resumeLabel}
        </Button>
      )}
    </div>
  );
}

interface PreviewAreaProps {
  mode: "audio" | "video" | "screen";
  recordingState: "idle" | "recording" | "paused";
  hasPermission: boolean | null;
  onRequestPermission: () => void;
  elapsedTime: number;
  stream: MediaStream | null;
  isPaused: boolean;
  selectedDeviceId?: string;
  micDisconnectError?: string | null;
  micMutedError?: string | null;
  onResume?: () => void;
  cameraStream?: MediaStream | null;
  /** Engine callbacks forwarded to AudioWaveformRecording for mic plugin registration. */
  onAttachMic?: (plugin: WavesurferMicPlugin) => void;
  onDetachMic?: () => void;
  className?: string;
}

export function PreviewArea({
  mode,
  recordingState,
  hasPermission,
  onRequestPermission,
  elapsedTime,
  stream,
  isPaused,
  selectedDeviceId,
  micDisconnectError = null,
  micMutedError = null,
  onResume,
  cameraStream = null,
  onAttachMic,
  onDetachMic,
  className,
}: PreviewAreaProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    if (stream) {
      videoEl.srcObject = stream;
      videoEl.play().catch(() => {});
    } else {
      videoEl.srcObject = null;
    }
  }, [stream]);

  const timer = formatClock(elapsedTime, { padMinutes: false });

  const resumeLabel = t("RECORD.RESUME", "Resume");
  const banner: { variant: MicBannerVariant; message: string } | null =
    micDisconnectError
      ? {
          variant: "hardware",
          message: t(
            "RECORD.MIC_DISCONNECTED_BANNER",
            "Microphone disconnected. Reconnect and tap Resume.",
          ),
        }
      : micMutedError
        ? {
            variant: "mute",
            message: t(
              "RECORD.MIC_MUTED_BANNER",
              "Microphone muted. Unmute and tap Resume.",
            ),
          }
        : null;

  if (hasPermission === false) {
    return (
      <div
        className={cn(
          PREVIEW_FRAME_CLASS,
          "flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-border bg-muted/50",
          className,
        )}
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accent">
          <ShieldAlert className="h-7 w-7 text-muted-foreground" />
        </div>
        <div className="text-center">
          <p className="text-sm font-semibold text-foreground">{t("RECORD.PERMISSION_REQUIRED", "Permission required")}</p>
          <p className="mt-1 max-w-xs text-xs text-muted-foreground">
            {t("RECORD.PERMISSION_DESC", "Allow access to your {{device}} to start recording.", {
              device: mode === "video"
                ? t("RECORD.CAMERA_AND_MIC", "camera and microphone")
                : mode === "screen"
                  ? t("RECORD.SCREEN_AND_MIC", "screen and microphone")
                  : t("RECORD.MICROPHONE", "microphone"),
            })}
          </p>
        </div>
        <Button onClick={onRequestPermission} size="sm">
          {t("RECORD.GRANT_ACCESS", "Grant access")}
        </Button>
      </div>
    );
  }

  if (mode === "screen") {
    return (
      <div
        className={cn(
          PREVIEW_FRAME_CLASS,
          "relative overflow-hidden rounded-2xl bg-foreground/5",
          className
        )}
      >
        {stream ? (
          <Suspense fallback={<div className="h-full w-full rounded-2xl bg-foreground/5 animate-pulse" />}>
            <ScreenShareComposer
              screenStream={stream}
              cameraStream={cameraStream}
              onComposedStream={() => {}}
            />
          </Suspense>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-foreground/5 to-foreground/10">
            <Monitor className="h-16 w-16 text-muted-foreground/40" aria-hidden="true" />
          </div>
        )}

        {recordingState !== "idle" && (
          <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-foreground/70 px-3 py-1.5 backdrop-blur-sm">
            <div
              className={cn(
                "h-2.5 w-2.5 rounded-full bg-danger",
                recordingState === "recording" && "animate-pulse"
              )}
            />
            <span className="text-xs font-semibold tabular-nums text-background">
              {timer}
            </span>
          </div>
        )}

        {recordingState === "paused" && (
          <div className="absolute inset-0 flex items-center justify-center bg-foreground/20 backdrop-blur-sm">
            <span className="text-lg font-semibold text-background">{t("RECORD.STATUS_PAUSED", "Paused")}</span>
          </div>
        )}
      </div>
    );
  }

  if (mode === "video") {
    return (
      <div className={cn("flex flex-col gap-3", className)}>
        {banner && (
          <MicStatusBanner
            variant={banner.variant}
            message={banner.message}
            onResume={onResume}
            resumeLabel={resumeLabel}
          />
        )}
        <div className={cn(PREVIEW_FRAME_CLASS, "relative overflow-hidden rounded-2xl bg-foreground/5")}>
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            webkit-playsinline=""
            x-webkit-airplay="deny"
            disablePictureInPicture
            style={VIDEO_STYLE}
            className="h-full w-full"
            data-testid="recorder-video-preview-live"
          />

          {!stream && (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-foreground/5 to-foreground/10">
              <Camera className="h-16 w-16 text-muted-foreground/40" />
            </div>
          )}

          {recordingState !== "idle" && (
            <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-foreground/70 px-3 py-1.5 backdrop-blur-sm">
              <div
                className={cn(
                  "h-2.5 w-2.5 rounded-full bg-danger",
                  recordingState === "recording" && "animate-pulse",
                )}
              />
              <span className="text-xs font-semibold tabular-nums text-background">
                {timer}
              </span>
            </div>
          )}

          {recordingState === "paused" && (
            <div className="absolute inset-0 flex items-center justify-center bg-foreground/20 backdrop-blur-sm">
              <span className="text-lg font-semibold text-background">{t("RECORD.STATUS_PAUSED", "Paused")}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Audio mode
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {banner && (
        <MicStatusBanner
          variant={banner.variant}
          message={banner.message}
          onResume={onResume}
          resumeLabel={resumeLabel}
        />
      )}
      {recordingState !== "idle" ? (
        <div className="flex w-full flex-col items-center gap-4">
          <span
            className="font-heading text-[64px] font-medium leading-[80px] tabular-nums text-foreground"
            role="timer"
            data-testid="recorder-timer"
          >
            {timer}
          </span>
          <div className="w-full max-w-md">
            <Suspense fallback={<div className="h-24 w-full rounded-lg bg-foreground/5 animate-pulse" />}>
              <AudioWaveformRecording
                isActive={recordingState === "recording"}
                isPaused={isPaused}
                selectedDeviceId={selectedDeviceId}
                onAttachMic={onAttachMic}
                onDetachMic={onDetachMic}
              />
            </Suspense>
          </div>
        </div>
      ) : (
        <div className="flex w-full items-center justify-center rounded-2xl border border-border bg-card py-10">
          <span className="text-sm text-muted-foreground">
            {t("RECORD.READY_TO_RECORD", "Ready to record")}
          </span>
        </div>
      )}
    </div>
  );
}
